/**
 * Загрузка и сохранение личного пространства.
 *
 * Сохранение выполняется явным вызовом, а не автоматически на каждое
 * изменение: запись в IndexedDB не должна попадать в путь обработки нажатия
 * (NFR-P-01).
 */

import { useCallback, useEffect, useState } from 'react';
import type { Workspace } from '@type2x/feature-personal-workspace';
import { assertOwnership } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';

/** Состояние загрузки пространства. */
export interface WorkspaceState {
  readonly workspace: Workspace | null;
  readonly loading: boolean;
  /** Сохраняет и делает пространство текущим. */
  readonly commit: (next: Workspace) => Promise<void>;
  /** Забывает пространство целиком: удаление аккаунта не оставляет следов. */
  readonly forget: () => Promise<void>;
}

export function useWorkspace(runtime: Runtime): WorkspaceState {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    runtime.repository
      .load(runtime.installationId)
      .then((loaded) => {
        if (cancelled) return;
        // Проверка владения защищает от смешивания пространств в одном браузере.
        setWorkspace(loaded === null ? null : assertOwnership(loaded, runtime.installationId));
      })
      .catch(() => {
        if (!cancelled) setWorkspace(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [runtime]);

  const commit = useCallback(
    async (next: Workspace) => {
      setWorkspace(next);
      try {
        await runtime.repository.save(next);
      } catch {
        // Сохранение не удалось: тренировка продолжается, теряется только история.
      }
    },
    [runtime],
  );

  const forget = useCallback(async () => {
    setWorkspace(null);
    try {
      await runtime.repository.clear(runtime.installationId);
    } catch {
      // Локальная запись не удалилась: аккаунта на сервере уже нет, и
      // показывать пользователю ошибку об этом бессмысленно.
    }
  }, [runtime]);

  return { workspace, loading, commit, forget };
}
